/**
 * Reports page — End-of-Day operational/financial roll-up + Vaccination
 * Compliance dashboard for Becky/Ted (issue #32).
 *
 * Tabs:
 *   - End of Day (date-picked) — dogs in/out, invoices settled vs
 *     outstanding, gratuity per groomer, payment-method breakdown.
 *   - Vaccination Compliance — every pet with an expired or
 *     unverified vaccination, grouped by vaccine type, sorted by the
 *     soonest next-visit date.
 *
 * Drill-in: the Dashboard's "vax up-to-date %" insight card links to
 * `/reports?tab=vax` so a single click lands on the non-compliant pet
 * list (AC3).
 */
import { useCallback, useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  fetchEndOfDayReport,
  fetchVaccinationComplianceReport,
  readSession,
  todayIsoDate,
  humanizeApiError,
  type EndOfDayReport as EndOfDayReportData,
  type VaccinationComplianceReport as VaxReportData,
} from '../lib/api.js';
import { EndOfDayReport } from '../components/reports/EndOfDayReport.js';
import { VaccinationComplianceReport } from '../components/reports/VaccinationComplianceReport.js';
// Sawyer 08 meds-feeding (A1) — "you can also find that under reports".
import { MedsFeedingReportTab } from '../components/meds-feeding/MedsFeedingReport.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';

type ReportsProps = {
  config: ClientConfig;
};

type Tab = 'end-of-day' | 'vax' | 'meds-feeding';

type EndOfDayState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: EndOfDayReportData };

type VaxState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: VaxReportData };

const TAB_LABELS: Record<Tab, string> = {
  'end-of-day': 'End of Day',
  vax: 'Vaccination Compliance',
  'meds-feeding': 'Meds & feeding',
};

/** Sawyer 08 — the Meds & feeding tab only for roles that hold a pets read (never the accountant). */
function reportTabsFor(roles: ReadonlyArray<string>): readonly Tab[] {
  const canSeeCare =
    rolesHavePermission(roles, 'pets:read:all') || rolesHavePermission(roles, 'pets:read:self');
  return canSeeCare ? ['end-of-day', 'vax', 'meds-feeding'] : ['end-of-day', 'vax'];
}

function parseTab(raw: string | null): Tab {
  return raw === 'vax' ? 'vax' : raw === 'meds-feeding' ? 'meds-feeding' : 'end-of-day';
}

export function Reports({ config }: ReportsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [searchParams, setSearchParams] = useSearchParams();
  const REPORT_TABS = reportTabsFor(effectiveRolesOf(readSession().staff));
  const [tab, setTab] = useState<Tab>(() => {
    const wanted = parseTab(searchParams.get('tab'));
    return REPORT_TABS.includes(wanted) ? wanted : 'end-of-day';
  });
  const [date, setDate] = useState<string>(() => todayIsoDate());
  const [endOfDay, setEndOfDay] = useState<EndOfDayState>({ kind: 'loading' });
  const [vax, setVax] = useState<VaxState>({ kind: 'loading' });
  // Sawyer 09 pet-inactivity (D1) — `?inactive=1` beside `?tab=vax`; a refetch keeps the last report on screen.
  const includeInactive = searchParams.get('inactive') === '1';
  const [vaxRefreshing, setVaxRefreshing] = useState(false);
  const setIncludeInactive = useCallback(
    (next: boolean) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set('inactive', '1');
      else params.delete('inactive');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Keep ?tab= in the URL in sync with the active tab (deep-linkable
  // for the Dashboard insight-card drill).
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  const loadEndOfDay = useCallback(async () => {
    setEndOfDay({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setEndOfDay({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const data = await fetchEndOfDayReport(apiUrl, token, date);
      setEndOfDay({ kind: 'ready', data });
    } catch (err) {
      setEndOfDay({
        kind: 'error',
        message: humanizeApiError(err instanceof Error ? err.message : null),
      });
    }
  }, [apiUrl, date]);

  const loadVax = useCallback(async () => {
    setVax((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    setVaxRefreshing(true);
    const { token } = readSession();
    if (!token) {
      setVax({ kind: 'error', message: 'Not authenticated' });
      setVaxRefreshing(false);
      return;
    }
    try {
      const data = await fetchVaccinationComplianceReport(apiUrl, token, { includeInactive });
      setVax({ kind: 'ready', data });
    } catch (err) {
      setVax({
        kind: 'error',
        message: humanizeApiError(err instanceof Error ? err.message : null),
      });
    } finally {
      setVaxRefreshing(false);
    }
  }, [apiUrl, includeInactive]);

  useEffect(() => {
    if (tab === 'end-of-day') {
      void loadEndOfDay();
    } else if (tab === 'vax') {
      void loadVax();
    }
  }, [tab, loadEndOfDay, loadVax]);

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentTab: Tab,
  ): void => {
    const currentIndex = REPORT_TABS.indexOf(currentTab);
    let nextIndex: number | null = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % REPORT_TABS.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + REPORT_TABS.length) % REPORT_TABS.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = REPORT_TABS.length - 1;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = REPORT_TABS[nextIndex];
    if (!nextTab) return;
    setTab(nextTab);
    document.getElementById(`reports-tab-${nextTab}`)?.focus();
  };

  return (
    <section className="ferro-admin-page">
      <header className="ferro-toolbar">
        <h1 className="ferro-section__title" style={{ marginBottom: 0 }}>
          Reports
        </h1>
        <div
          className="ferro-tabs"
          role="tablist"
          aria-label="Report category"
        >
          {REPORT_TABS.map((t) => (
            <button
              key={t}
              id={`reports-tab-${t}`}
              type="button"
              role="tab"
              aria-selected={tab === t}
              aria-controls={`reports-panel-${t}`}
              tabIndex={tab === t ? 0 : -1}
              className={`ferro-tabs__tab${
                tab === t ? ' ferro-tabs__tab--active' : ''
              }`}
              onClick={() => setTab(t)}
              onKeyDown={(event) => handleTabKeyDown(event, t)}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </header>

      {tab === 'end-of-day' ? (
        <section
          id="reports-panel-end-of-day"
          role="tabpanel"
          aria-labelledby="reports-tab-end-of-day"
          tabIndex={0}
        >
          <form
            className="ferro-toolbar"
            onSubmit={(e) => e.preventDefault()}
            aria-label="End-of-day controls"
          >
            <label className="ferro-field" style={{ margin: 0 }}>
              <span className="ferro-field__label">Date</span>
              <input
                type="date"
                className="ferro-field__input"
                value={date}
                onChange={(e) => setDate(e.target.value || todayIsoDate())}
              />
            </label>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setDate(todayIsoDate())}
            >
              Today
            </button>
          </form>

          {endOfDay.kind === 'loading' ? (
            <p className="ferro-muted" role="status">
              Loading end-of-day report…
            </p>
          ) : null}

          {endOfDay.kind === 'error' ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{endOfDay.message}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void loadEndOfDay()}
              >
                Retry
              </button>
            </div>
          ) : null}

          {endOfDay.kind === 'ready' ? (
            <EndOfDayReport data={endOfDay.data} />
          ) : null}
        </section>
      ) : tab === 'vax' ? (
        <section
          id="reports-panel-vax"
          role="tabpanel"
          aria-labelledby="reports-tab-vax"
          tabIndex={0}
        >
          {vax.kind === 'loading' ? (
            <p className="ferro-muted" role="status">
              Loading vaccination compliance report…
            </p>
          ) : null}

          {vax.kind === 'error' ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{vax.message}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void loadVax()}
              >
                Retry
              </button>
            </div>
          ) : null}

          {vax.kind === 'ready' ? (
            <VaccinationComplianceReport
              data={vax.data}
              includeInactive={includeInactive}
              onIncludeInactiveChange={setIncludeInactive}
              busy={vaxRefreshing}
            />
          ) : null}
        </section>
      ) : null}
      {/* Sawyer 08 meds-feeding (A1) — the list owns its own fetch and states. */}
      {tab === 'meds-feeding' ? (
        <section
          id="reports-panel-meds-feeding"
          role="tabpanel"
          aria-labelledby="reports-tab-meds-feeding"
          tabIndex={0}
        >
          <MedsFeedingReportTab
            apiUrl={apiUrl}
            date={date}
            dateControl={
              <label className="ferro-field" style={{ margin: 0 }}>
                <span className="ferro-field__label">Date</span>
                <input
                  type="date"
                  className="ferro-field__input"
                  value={date}
                  onChange={(e) => setDate(e.target.value || todayIsoDate())}
                  data-testid="reports-meds-feeding-date"
                />
              </label>
            }
          />
        </section>
      ) : null}
    </section>
  );
}
